import * as IkeModel from '@ikegps/core-shared';
// @todo deprecate use of time-stamp and use moment
import timestamp from 'time-stamp';
import { DateInputState } from '../../../components/input/date.input.state';
import { Input } from '../../../components/input/input';
import { Icon } from '../../../components/icon';
import { MaterialIcon } from '../../../components/icon/icons';
import { addStyles, DefaultTheme } from '../../../components/theme';
import { T, Translate } from '../../../i18n/index';
import { m, type MithrilVNode } from '../../../mithril';
import { Services } from '../../../services';
import { CollectionEditCtrl } from '../collection.view.ctrl';
import type { CollectionFieldView } from './field.view';

const CSS = {
	container: 'c-DateField',
	field: 'c-DateField__field',
	picker: 'c-DateField__picker',
	// The native input drives the calendar popup but is not shown; the text field
	// renders the value in the user's configured format.
	native: 'c-DateField__native',
};

addStyles({
	[CSS.container]: {
		position: 'relative',
		display: 'flex',
		'align-items': 'flex-end',
		width: '100%',
	},
	[CSS.field]: {
		flex: '1',
		'min-width': '0',
	},
	[CSS.picker]: {
		background: 'none',
		border: 'none',
		outline: 'none',
		padding: '0',
		cursor: 'pointer',
		color: DefaultTheme.font.color.supporting,
		'& i': {
			'font-size': '20px',
		},
	},
	[CSS.native]: {
		position: 'absolute',
		right: '0',
		bottom: '0',
		width: '1px',
		height: '1px',
		opacity: '0',
		border: '0',
		padding: '0',
		'pointer-events': 'none',
	},
});

export const DateFieldView: CollectionFieldView<IkeModel.CollectionModelFieldDate> = {
	viewInline(field: IkeModel.CollectionModelFieldDate): string {
		if (!field.hasValue()) {
			return '';
		}

		const date = new Date(field.value);

		if (field.value == null || isNaN(date.getTime())) {
			return Translate(T.DATE_INVALID);
		}

		return timestamp(Services.UserSettings.dateFormat, date);
	},

	viewDetail(field: IkeModel.CollectionModelFieldDate): MithrilVNode {
		return null;
	},

	viewEdit(field: IkeModel.CollectionModelFieldDate, ctrl: CollectionEditCtrl): MithrilVNode {
		const existing = ctrl.changeState.getState(field.id);
		let inputState: DateInputState;
		if (existing instanceof DateInputState) {
			inputState = existing;
		} else {
			inputState = new DateInputState(field.schema.displayName);
			inputState.setPlaceholder(Services.UserSettings.dateFormat);
			inputState.onValue(() => field.Mutator.manual(inputState.isoValue));
			ctrl.changeState.addState(field.id, inputState);
		}

		const modelValue = field.hasValue() ? field.value : null;
		if (!inputState.focused && inputState.isoValue !== modelValue) {
			inputState.setIsoValue(modelValue);
		}

		const onPick = (e: Event) => {
			const value = (e.target as HTMLInputElement).value || null;
			inputState.setIsoValue(value);
			inputState.setTouched();
			// Update the model synchronously: the text input is not focused while
			// picking, so the re-sync above would otherwise revert the pick before
			// the async onValue reaction can write it through.
			field.Mutator.manual(value);
		};

		const openPicker = (e: Event) => {
			const button = e.currentTarget as HTMLElement;
			const native = button.parentElement?.querySelector<HTMLInputElement>(`.${CSS.native}`);
			if (native != null && typeof native.showPicker === 'function') {
				native.showPicker();
			}
		};

		return m('div', { className: CSS.container }, [
			m('div', { className: CSS.field }, Input.view(inputState)),
			m(
				'button',
				{ type: 'button', className: CSS.picker, title: Translate(T.DATE_PICK), onclick: openPicker },
				Icon.view(MaterialIcon.DATE)
			),
			m('input', {
				type: 'date',
				className: CSS.native,
				value: inputState.nativeValue,
				onchange: onPick,
				tabindex: -1,
				'aria-hidden': 'true',
			}),
		]);
	},
};
